import { formatCoordinate, type PointDto, type WaypointDto } from '@datum/shared'
import { PencilLine, Plus } from 'lucide-react'
import { SyncBadge } from './SyncBadge'

interface TimelineProps {
  points: PointDto[]
  waypoints: WaypointDto[]
  selectedId: string | null
  loading: boolean
  offline: boolean
  onSelect: (id: string) => void
  onNew: () => void
}

function timeAgo(ts: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (seconds < 60) return 'now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

export function Timeline({ points, waypoints, selectedId, loading, offline, onSelect, onNew }: TimelineProps) {
  const waypointNames = new Map(waypoints.map((w) => [w.id, w.name]))

  return (
    <section className="flex min-h-0 flex-1 flex-col" aria-label="Notes timeline">
      <header className="flex items-center justify-between gap-2 border-b border-edge px-4 py-2">
        <div className="flex items-baseline gap-2">
          <h2 className="mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">Notes</h2>
          <span className="mono text-[10px] text-zinc-600">
            {loading ? '…' : `${points.length}${offline ? ' · cached' : ''}`}
          </span>
        </div>
        <button
          type="button"
          onClick={onNew}
          className="group flex items-center gap-1.5 rounded border border-edge bg-ink-850 px-2 py-1 text-[11px] text-zinc-300 hover:border-edge-strong hover:bg-ink-800 hover:text-zinc-50"
          title="New note at map center"
        >
          <Plus size={13} className="text-sync" />
          New
          <kbd className="mono ml-1 rounded border border-edge bg-ink-900 px-1 text-[9px] text-zinc-500 group-hover:text-zinc-400">
            N
          </kbd>
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {points.length === 0 && !loading && (
          <div className="px-4 py-8 text-center">
            <p className="text-[13px] text-zinc-500">No notes in this viewport.</p>
            <p className="mono mt-1.5 text-[10px] text-zinc-600">pan the map, or press N</p>
          </div>
        )}
        <ul>
          {points.map((point) => {
            const selected = point.id === selectedId
            return (
              <li key={point.id} className="relative">
                <button
                  type="button"
                  onClick={() => onSelect(point.id)}
                  aria-current={selected}
                  className={`flex w-full flex-col gap-1 border-b border-edge px-4 py-2.5 text-left transition-colors ${
                    selected ? 'bg-ink-800' : 'hover:bg-ink-900'
                  }`}
                >
                  {selected && (
                    <span aria-hidden className="absolute left-0 top-0 h-full w-[2px] bg-sync" />
                  )}
                  <span className="flex w-full items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-zinc-100">
                      <PencilLine size={12} className="shrink-0 text-zinc-600" />
                      <span className="truncate">{point.title || 'Untitled note'}</span>
                    </span>
                    <span className="mono shrink-0 text-[10px] text-zinc-600">{timeAgo(point.updated_at)}</span>
                  </span>
                  <span className="flex w-full items-center justify-between gap-2">
                    <span className="mono truncate text-[10px] text-zinc-500">
                      {formatCoordinate(point.latitude, point.longitude)}
                      {point.waypoint_id && waypointNames.has(point.waypoint_id)
                        ? ` · ${waypointNames.get(point.waypoint_id)}`
                        : ''}
                    </span>
                    <SyncBadge pointId={point.id} />
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
